// ── BCH Rooms — presence (NIP-53 kind 10312) ──────────────────────
//
// The roster problem: in a serverless room nobody is authoritative about
// who is present. WebRTC `leave` messages are unreliable — closing a
// laptop, killing a tab or losing signal all exit without one — so a
// roster built from join/leave alone accumulates ghosts, which users
// read as "this app is broken" faster than any missing feature.
//
// NIP-53 kind 10312 fixes this by inverting the question: instead of
// waiting to be told someone left, each participant re-publishes a
// *replaceable* presence event on a timer. Presence that stops being
// refreshed ages out on its own. Absence is inferred from silence.
//
// The same event carries the raised-hand flag (`["hand","1"]`), so
// hand-raise needs no extra plumbing and is visible to other NIP-53
// clients too.

import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { getEventHash, type Event, type UnsignedEvent } from 'nostr-tools';
import { useNostrQuery } from './useNostrQuery';
import { getRelays } from './useNostrPublish';
import { startUnthrottledInterval, type UnthrottledInterval } from '../lib/workerTimer';
import {
  ROOM_PRESENCE_KIND, PRESENCE_INTERVAL_MS, PRESENCE_TIMEOUT_MS,
  activePresence, type PresenceEntry,
} from '../lib/rooms';

interface NostrSigner {
  signEvent(event: UnsignedEvent & { id?: string }): Promise<Event>;
}

export interface UseRoomPresenceOptions {
  /** Room `a` coordinate. */
  coordinate: string;
  /** Our pubkey, or undefined when not logged in. */
  selfPubkey: string | undefined;
  /** Publish our own presence (i.e. we have joined, not just browsing). */
  publishing: boolean;
  /** Our current raised-hand state, mirrored into the presence event. */
  handRaised?: boolean;
}

export interface UseRoomPresenceResult {
  /** Everyone with fresh presence, oldest first (join order). */
  present: PresenceEntry[];
  /** Publish presence immediately (e.g. right after raising a hand). */
  refresh: () => void;
}

export function useRoomPresence({
  coordinate, selfPubkey, publishing, handRaised = false,
}: UseRoomPresenceOptions): UseRoomPresenceResult {
  // Presence events are public and replaceable. No author filter is
  // needed or possible here: any pubkey may claim to be in a room, and
  // claiming presence *is* joining. Each event only ever describes its
  // own author, so there is no trust boundary to enforce.
  const since = useMemo(
    () => Math.floor(Date.now() / 1000) - Math.ceil(PRESENCE_TIMEOUT_MS / 1000) * 2,
    // Recompute when the room changes so a stale `since` isn't reused.
    [coordinate],
  );

  const { events, refetch } = useNostrQuery({
    filters: coordinate
      ? [{ kinds: [ROOM_PRESENCE_KIND], '#a': [coordinate], since, limit: 200 }]
      : [],
    enabled: !!coordinate,
  });

  // Presence expires with wall-clock time, not with new events, so the
  // list must be recomputed on a tick or departed users would linger
  // until the next unrelated event arrived.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!coordinate) return;
    const id = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(id);
  }, [coordinate]);

  const present = useMemo(
    () => activePresence(events, coordinate, now),
    [events, coordinate, now],
  );

  // ── Publish our own presence ────────────────────────────────────
  const handRef = useRef(handRaised);
  useEffect(() => { handRef.current = handRaised; }, [handRaised]);

  const publishPresence = useCallback(async () => {
    const signer = (window as unknown as { nostr?: NostrSigner }).nostr;
    if (!signer || !selfPubkey || !coordinate) return;

    const tags: string[][] = [['a', coordinate, '', 'root']];
    if (handRef.current) tags.push(['hand', '1']);

    const unsigned: UnsignedEvent = {
      kind: ROOM_PRESENCE_KIND,
      content: '',
      tags,
      created_at: Math.floor(Date.now() / 1000),
      pubkey: selfPubkey,
    };

    try {
      const signed = await signer.signEvent({
        ...unsigned, id: getEventHash(unsigned),
      });
      // Publish directly rather than through useNostrPublish: presence is
      // a heartbeat, so it must not enter the local event cache (it would
      // evict real content) and a failed beat is simply retried next tick.
      for (const url of getRelays().slice(0, 4)) {
        try {
          const ws = new WebSocket(url);
          ws.onopen = () => {
            try { ws.send(JSON.stringify(['EVENT', signed])); } catch { /* noop */ }
            setTimeout(() => { try { ws.close(); } catch { /* noop */ } }, 1500);
          };
          ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
        } catch { /* skip relay */ }
      }
    } catch { /* signer refused — next beat will retry */ }
  }, [selfPubkey, coordinate]);

  useEffect(() => {
    if (!publishing || !selfPubkey || !coordinate) return;
    // Beat immediately so we appear in our own roster without a delay.
    void publishPresence();
    // A phone that locks its screen or backgrounds the browser freezes
    // main-thread timers, which would stop the heartbeat and evict a user
    // who is still listening. A Web Worker timer keeps firing — the same
    // reason the live-stream heartbeat uses it.
    const timer: UnthrottledInterval = startUnthrottledInterval(
      PRESENCE_INTERVAL_MS,
      () => { void publishPresence(); },
    );
    return () => timer.stop();
  }, [publishing, selfPubkey, coordinate, publishPresence]);

  // Re-publish as soon as the hand state changes, so the flag propagates
  // in about a second instead of waiting for the next scheduled beat.
  useEffect(() => {
    if (!publishing) return;
    void publishPresence();
  }, [handRaised, publishing, publishPresence]);

  const refresh = useCallback(() => {
    void publishPresence();
    refetch();
  }, [publishPresence, refetch]);

  return { present, refresh };
}
